import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
// import ErrorBoundary from './components/common/ErrorBoundary'
// import { GlobalErrorProvider } from './components/providers/GlobalErrorProvider'
import { PerformanceProvider } from './components/providers/PerformanceProvider'
import { validateAndLogFrontendEnvironment } from './utils/env-validation'
import { AuthCore } from './core/AuthCore'
import AuthErrorBoundary from './components/common/AuthErrorBoundary'
import './index.css'
import { sanitizeErrorMessage } from './utils/errorSanitizer'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: (failureCount: number, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }
        return failureCount < 3
      },
    },
    mutations: {
      retry: 1,
    },
  },
})


// 🔒 MANDATORY ENVIRONMENT VALIDATION - DO NOT REMOVE!
// This ensures all required ENV variables are present before app starts
try {
  validateAndLogFrontendEnvironment('app-startup');
} catch (error) {
  console.error('❌ CRITICAL: Environment validation failed!', error);
  // Show user-friendly error instead of crashing (XSS-safe)
  const errorContainer = document.createElement('div');
  errorContainer.style.cssText = 'padding: 20px; text-align: center; font-family: Arial, sans-serif;';
  
  const errorTitle = document.createElement('h1');
  errorTitle.style.color = '#dc2626';
  errorTitle.textContent = 'Configuration Error';
  
  const errorMessage = document.createElement('p');
  errorMessage.textContent = 'The application is not properly configured. Please contact support.';
  
  const errorDetails = document.createElement('p');
  errorDetails.style.cssText = 'font-size: 12px; color: #666; margin-top: 20px;';
  errorDetails.textContent = `Error: ${sanitizeErrorMessage(error)}`;
  
  errorContainer.appendChild(errorTitle);
  errorContainer.appendChild(errorMessage);
  errorContainer.appendChild(errorDetails);
  
  document.body.innerHTML = ''; // Clear existing content safely
  document.body.appendChild(errorContainer);
  throw error; // Still throw to prevent further execution
}

// Initialize authentication system and render the app
(async () => {
  const authCore = AuthCore.getInstance();
  await authCore.initialize();

  // Render the app
  ReactDOM.createRoot(document.getElementById('root')!).render(
    // TEMPORARILY DISABLED: React.StrictMode for performance debugging
    // <React.StrictMode>
      <AuthErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <HelmetProvider>
              <PerformanceProvider 
                enabled={import.meta.env.VITE_ENVIRONMENT !== 'test'}
                logToConsole={import.meta.env.VITE_ENVIRONMENT === 'development'}
              >
                <App />
              </PerformanceProvider>
              <Toaster 
                position="bottom-right"
                expand={true}
                richColors
                closeButton
              />
            </HelmetProvider>
          </BrowserRouter>
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        </QueryClientProvider>
      </AuthErrorBoundary>
    // </React.StrictMode>,
  )
})();
